import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Heart,
  Sparkles,
  GraduationCap,
  Users,
  Compass,
  Award,
  Leaf,
  BookOpen,
  Sun,
  CheckCircle2,
} from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Sunflower, SunflowerMini } from "@/components/site/Sunflower";
import { SUNFLOWER_FIELD_URL, IBCCES_STAMP_URL, LOGO_URL } from "@/lib/assets";
import { api } from "@/lib/api";

const supportGroups = [
  {
    icon: Heart,
    title: "Parents",
    body: "From surviving daily meltdowns to a calm, regulated home. 1:1 coaching, routines, and real strategies that actually fit your week.",
    color: "bg-brand-sun/20 text-brand-sunDeep",
  },
  {
    icon: GraduationCap,
    title: "Schools",
    body: "Ready-to-use curriculum, staff training, and classroom redesign built on inclusion-first principles.",
    color: "bg-brand-leaf/20 text-brand-leafDeep",
  },
  {
    icon: Users,
    title: "Caregivers",
    body: "Practical tools for nannies, helpers, and extended family raising a neurodivergent child.",
    color: "bg-brand-terracotta/20 text-brand-rust",
  },
  {
    icon: Sparkles,
    title: "Corporates",
    body: "Neurodiversity awareness workshops for teams building inclusive workplaces and products.",
    color: "bg-brand-sky/40 text-brand-leafDeep",
  },
];

const pillars = [
  {
    icon: Leaf,
    title: "Structured Routines",
    body: "Predictability is a nervous system's best friend. We design routines that hold steady even on hard days.",
    accent: "bg-brand-leafSoft/30",
  },
  {
    icon: Heart,
    title: "Emotional Regulation",
    body: "Name it, locate it, move through it. Children learn to recognise signals before they turn into storms.",
    accent: "bg-brand-sun/30",
  },
  {
    icon: Sun,
    title: "Sensory Support",
    body: "From calm corners to sensory diets, we tailor the environment to the child — not the other way around.",
    accent: "bg-brand-petal/40",
  },
];

const programs = [
  {
    title: "1:1 Sessions",
    body: "In-person in Dubai or online. Tailored support for Autism and ADHD across ages 2–16.",
  },
  {
    title: "Group Classes",
    body: "Small online groups for emotional intelligence, communication, and skill-building.",
  },
  {
    title: "Workshops",
    body: "Parent workshops, school inclusion trainings, and corporate awareness sessions.",
  },
];

const credentials = [
  "Education Director — Early Childhood",
  "Neurodiversity Curriculum Specialist",
  "Autism Certified Professional (IBCCES)",
  "Illustrator & Children's Book Creator",
];

export default function Home() {
  const [featuredBook, setFeaturedBook] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          "/products/ebook-nurturing-sunflower-soul"
        );
        setFeaturedBook(data);
      } catch {
        // graceful fallback
      }
    })();
  }, []);

  return (
    <div data-testid="home-page" className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden petal-bg">
        {/* decorative sunflower top-left */}
        <div className="absolute -top-16 -left-20 opacity-60 pointer-events-none hidden md:block">
          <Sunflower size={260} className="animate-slowspin" />
        </div>
        <div className="absolute top-24 right-8 opacity-70 pointer-events-none hidden lg:block">
          <SunflowerMini size={48} className="animate-bob" />
        </div>

        <div className="relative grid lg:grid-cols-12 gap-10 px-6 md:px-12 lg:px-24 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-leafDeep bg-brand-honey/50 border border-brand-sun/40 rounded-full px-4 py-1.5 mb-6 w-fit">
                <SunflowerMini size={18} />
                Flying Calmly by NeuroBloom · Dubai
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="font-heading font-normal text-[2.75rem] sm:text-5xl lg:text-[4.5rem] leading-[1.02] tracking-tight text-brand-ink">
                A <span className="italic text-brand-leafDeep">calmer</span>,
                <br />
                <span className="sunray-underline">brighter path</span> through
                <br />
                ADHD and Autism.
              </h1>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-7 text-brand-muted text-base md:text-lg max-w-xl leading-relaxed font-body">
                Nada Zahabi is a Dubai-based neurodiversity specialist
                supporting families, schools, and caregivers with structured
                routines, emotional regulation, and sensory-smart practice.
                Real solutions — in warm, human language.
              </p>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link
                  data-testid="hero-book-session-btn"
                  to="/contact"
                  className="rounded-full bg-brand-sun hover:bg-brand-sunDeep text-brand-ink px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2 transition-all shadow-sunny hover:-translate-y-0.5"
                >
                  Contact <ArrowRight size={16} />
                </Link>
                <Link
                  data-testid="hero-explore-programs-btn"
                  to="/services"
                  className="rounded-full border-2 border-brand-leafDeep text-brand-leafDeep px-7 py-3 text-sm font-medium hover:bg-brand-leafDeep hover:text-brand-cream transition-colors"
                >
                  Explore Programs
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.38}>
              <p className="mt-12 text-sm tracking-[0.22em] uppercase text-brand-muted font-body flex items-center gap-3">
                <span className="h-px w-8 bg-brand-sun" />
                "Regulation before education."
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-5 relative">
            <Reveal delay={0.1}>
              <div className="relative rounded-[36px] overflow-hidden shadow-earthyLg border-4 border-brand-sun/40">
                <img
                  src={SUNFLOWER_FIELD_URL}
                  alt="Sunflower field at sunset — a symbol of growth and warmth"
                  className="w-full h-[440px] md:h-[560px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/45 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 bg-brand-cream/95 backdrop-blur-md rounded-2xl px-5 py-4 border border-brand-sun/40">
                  <p className="text-xs uppercase tracking-[0.2em] text-brand-leafDeep font-semibold">
                    Inclusion is not placement
                  </p>
                  <p className="text-brand-ink text-sm mt-1 font-body">
                    — it is preparation.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Dotted line divider */}
        <div className="dotted-line h-1 mx-24" />
      </section>

      {/* WHO WE SUPPORT */}
      <section className="section-pad relative overflow-hidden">
        <div className="absolute top-10 right-10 opacity-25 pointer-events-none hidden lg:block">
          <Sunflower size={180} className="animate-slowspin" />
        </div>
        <SectionHeading
          eyebrow="Who we support"
          title="Built for the people raising neurodivergent children."
          intro="Whether you're a parent up at 5am, a teacher redesigning a classroom, or a team building inclusive products — Nada meets you where you are."
        />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {supportGroups.map((g, i) => (
            <Reveal key={g.title} delay={i * 0.08}>
              <div
                data-testid={`support-card-${g.title.toLowerCase()}`}
                className="h-full sunny-card rounded-[28px] p-8 border-2 border-brand-line hover:border-brand-sun/50 hover:-translate-y-1 hover:shadow-sunny transition-all duration-500"
              >
                <div className={`h-12 w-12 rounded-2xl ${g.color} grid place-items-center mb-6`}>
                  <g.icon size={22} />
                </div>
                <h3 className="font-heading text-2xl mb-3">{g.title}</h3>
                <p className="text-sm text-brand-muted leading-relaxed font-body">
                  {g.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SIGNATURE APPROACH */}
      <section className="section-pad bg-brand-ivory relative overflow-hidden">
        <div className="absolute bottom-0 right-0 opacity-20 pointer-events-none hidden md:block">
          <Sunflower size={280} showLeaves className="animate-slowspin" />
        </div>
        <div className="relative grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Signature approach"
              title="Regulation first. Then everything else becomes possible."
              intro="Our framework rests on three foundations — each one practical, observable, and teachable. No mystery. No magic. Just the quiet science of helping a child settle."
            />
          </div>
          <div className="lg:col-span-7 grid gap-6">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div
                  data-testid={`pillar-${p.title.toLowerCase().replace(/\s/g, "-")}`}
                  className="bg-white rounded-[28px] p-8 border-2 border-brand-line hover:border-brand-sun/50 flex gap-6 items-start transition-colors"
                >
                  <div className={`h-14 w-14 rounded-2xl ${p.accent} text-brand-ink grid place-items-center shrink-0`}>
                    <p.icon size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl mb-2">{p.title}</h3>
                    <p className="text-sm text-brand-muted leading-relaxed font-body">
                      {p.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="section-pad">
        <SectionHeading
          eyebrow="Programs"
          title="Support that adapts to your child, your family, your school."
        />
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {programs.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="sunny-card rounded-[28px] p-8 border-2 border-brand-line h-full hover:border-brand-sun/50 transition-colors relative overflow-hidden">
                <div className="absolute -top-6 -right-6 opacity-10">
                  <Sunflower size={110} />
                </div>
                <p className="relative font-brand text-3xl text-brand-sun mb-4">
                  0{i + 1}
                </p>
                <h3 className="relative font-heading text-2xl mb-3">
                  {p.title}
                </h3>
                <p className="relative text-sm text-brand-muted leading-relaxed font-body">
                  {p.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10">
          <Link
            data-testid="home-see-all-services-btn"
            to="/services"
            className="inline-flex items-center gap-2 text-sm text-brand-leafDeep hover:text-brand-sunDeep font-medium"
          >
            See all services <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* DIGITAL PRODUCTS PREVIEW — featured book */}
      <section className="section-pad bg-brand-honey/30 relative overflow-hidden">
        <div className="absolute -top-16 -left-16 opacity-30 pointer-events-none">
          <Sunflower size={220} className="animate-slowspin" />
        </div>
        <div className="absolute bottom-12 right-12 opacity-25 pointer-events-none hidden lg:block">
          <Sunflower size={160} showLeaves className="animate-slowspin" />
        </div>

        <div className="relative grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Cover artwork */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Reveal>
              <div className="relative">
                {/* decorative back shadow */}
                <div className="absolute -inset-6 bg-brand-sun/15 rounded-[36px] blur-2xl" />
                <Link
                  to={featuredBook ? `/shop/${featuredBook.id}` : "/shop"}
                  data-testid="home-featured-book-cover"
                  className="relative block rounded-[28px] overflow-hidden border-4 border-brand-sun/50 bg-white aspect-[4/5] grid place-items-center p-6 shadow-earthyLg hover:-translate-y-2 hover:rotate-[-1deg] transition-transform duration-500"
                >
                  {featuredBook ? (
                    <img
                      src={featuredBook.image}
                      alt={`${featuredBook.title} book cover`}
                      className="max-h-full max-w-full object-contain drop-shadow-[0_18px_40px_rgba(44,36,22,0.22)]"
                    />
                  ) : (
                    <div className="text-brand-muted">Loading…</div>
                  )}
                  {featuredBook && (
                    <span className="absolute top-4 left-4 bg-brand-sun text-brand-ink text-[11px] uppercase tracking-[0.22em] font-semibold px-3 py-1.5 rounded-full">
                      Bestseller
                    </span>
                  )}
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Copy */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-leafDeep bg-white border border-brand-sun/40 rounded-full px-4 py-1.5 mb-6 w-fit">
              <SunflowerMini size={18} />
              From the shop · Featured
            </span>
            <h2 className="font-heading font-normal text-3xl sm:text-4xl lg:text-[3.25rem] leading-[1.05] tracking-tight">
              {featuredBook?.title || "Nurturing a Sunflower Soul"}
            </h2>
            {featuredBook?.subtitle && (
              <p className="mt-3 text-brand-muted italic text-lg font-body">
                {featuredBook.subtitle}
              </p>
            )}
            <p className="mt-6 text-brand-muted text-base md:text-lg leading-relaxed font-body max-w-xl">
              {featuredBook?.synopsis ||
                "Nada Zahabi's flagship 88-page guidebook for parents raising People of Determination — sensory-friendly toys, room design, nutrition, outdoor activities, and more."}
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-xl">
              {(featuredBook?.benefits || []).slice(0, 4).map((b) => (
                <div key={b} className="flex items-start gap-2 text-sm text-brand-ink font-body">
                  <CheckCircle2 size={16} className="text-brand-sunDeep shrink-0 mt-0.5" />
                  <span>{b}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-5">
              {featuredBook && (
                <p className="font-brand text-3xl text-brand-leafDeep">
                  ${featuredBook.price.toFixed(2)}{" "}
                  <span className="text-base text-brand-muted">
                    ({(featuredBook.currency || "usd").toUpperCase()})
                  </span>
                </p>
              )}
              <Link
                data-testid="home-featured-book-buy-btn"
                to={featuredBook ? `/shop/${featuredBook.id}` : "/shop"}
                className="rounded-full bg-brand-ink hover:bg-brand-leafDeep text-brand-cream px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 transition-colors shadow-earthy"
              >
                Get the book <ArrowRight size={16} />
              </Link>
              <Link
                data-testid="home-shop-all-btn"
                to="/shop"
                className="rounded-full border-2 border-brand-ink/15 px-6 py-3 text-sm font-medium hover:border-brand-sun bg-white transition-colors inline-flex items-center gap-2"
              >
                Browse all books <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="section-pad bg-brand-leafDeep text-brand-cream grain relative overflow-hidden">
        <div className="absolute -top-20 -right-20 opacity-30 pointer-events-none">
          <Sunflower size={320} showLeaves className="animate-slowspin" />
        </div>
        <div className="relative z-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-honey bg-white/10 border border-brand-honey/40 rounded-full px-4 py-1.5 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-sun" />
              Credentials
            </span>
            <h2 className="font-heading font-normal text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
              Trained. Certified.
              <br />
              <span className="sunray-underline text-brand-honey">Trusted</span>{" "}
              by Dubai families and schools.
            </h2>
            <p className="mt-6 text-brand-cream/85 leading-relaxed max-w-xl font-body">
              Nada Zahabi — a <span className="text-brand-honey font-semibold">Canadian educator</span> based in Dubai — blends early-childhood leadership, IBCCES-certified autism specialism, and creative practice to support children holistically.
            </p>

            {/* IBCCES stamp badge */}
            <div className="mt-8 inline-flex items-center gap-4 bg-white/8 border border-white/15 rounded-2xl p-4 pr-6">
              <img
                data-testid="home-ibcces-stamp"
                src={IBCCES_STAMP_URL}
                alt="IBCCES Autism Certificate"
                className="h-20 w-20 object-contain rounded-lg bg-white/95 p-1"
              />
              <div>
                <p className="font-heading text-lg text-brand-cream">IBCCES Autism Certified</p>
                <p className="text-xs uppercase tracking-[0.2em] text-brand-honey mt-1 font-semibold">
                  Globally recognised credential
                </p>
                <Link
                  to="/certifications"
                  data-testid="home-cred-learn-more"
                  className="text-xs text-brand-cream/80 hover:text-brand-sun mt-2 inline-flex items-center gap-1 font-body underline"
                >
                  Learn more about IBCCES
                </Link>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 grid sm:grid-cols-2 gap-4">
            {credentials.map((c, i) => (
              <Reveal key={c} delay={i * 0.08}>
                <div className="bg-white/8 border border-white/15 rounded-2xl p-6 flex items-start gap-3 hover:bg-white/12 transition-colors">
                  <Award size={18} className="text-brand-sun shrink-0 mt-1" />
                  <span className="text-sm font-body">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad">
        <div className="relative bg-gradient-to-br from-brand-sun/30 via-brand-honey/40 to-brand-leafSoft/30 rounded-[36px] p-10 md:p-16 border-2 border-brand-sun/40 text-center overflow-hidden">
          <div className="absolute -top-10 -left-10 opacity-30">
            <Sunflower size={180} className="animate-slowspin" />
          </div>
          <div className="absolute -bottom-10 -right-10 opacity-30">
            <Sunflower size={180} showLeaves className="animate-slowspin" />
          </div>
          <div className="relative">
            <img
              data-testid="cta-brand-logo"
              src={LOGO_URL}
              alt="Nada's Kids Garden"
              className="h-36 md:h-44 w-auto object-contain mx-auto mb-6"
            />
            <SectionHeading
              align="center"
              eyebrow="Ready when you are"
              title="Let's build a calmer, sunnier week for your child."
              intro="Book a 30-minute discovery session with Nada to map your next steps — practical, specific, and made for your family."
            />
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link
                data-testid="cta-book-session-btn"
                to="/contact"
                className="rounded-full bg-brand-ink text-brand-cream px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2 hover:bg-brand-leafDeep transition-colors shadow-earthy"
              >
                Contact <ArrowRight size={16} />
              </Link>
              <Link
                data-testid="cta-contact-btn"
                to="/contact"
                className="rounded-full bg-white border-2 border-brand-ink/15 px-7 py-3.5 text-sm font-medium hover:border-brand-sun transition-colors"
              >
                Contact Nada
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
